import * as React from 'react'

import { generatePath } from 'react-router-dom'
import {
  FaIcon,
  faCalendarRegular,
  faCalendarCheckRegular,
  faIdBadge,
} from '@/lib/fa'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'

import { SelectionGroup } from '@/modules/drivers/context/TableContext'
import { driversRoutesMap } from '@/modules/drivers/routes'
import { TimesheetRowData } from '../../views/Group.types'
import Tooltip from '@/modules/drivers/shared/ToolTip'
import { withRouter, RouteComponentProps } from '@/app/navigation/withRouter'

interface GroupViewRowProps extends RouteComponentProps {
  data: TimesheetRowData
  selectedIds?: SelectionGroup
  isSelectedAny?: boolean
  companyId: string
  selectedYear?: number
  history: any
  style?: React.CSSProperties
}

function getPolishMonthName(monthIndex: number) {
  return new Intl.DateTimeFormat('pl-PL', { month: 'long' }).format(
    new Date(2024, monthIndex, 1),
  )
}

/**
 * Group view row component (widok grupowy)
 * Displays driver information with calculation status, costs, gross total, and last calculation date
 */
const GroupViewRow: React.FC<GroupViewRowProps> = ({
  data,
  selectedIds,
  isSelectedAny = false,
  companyId,
  selectedYear,
  history,
  style,
}) => {
  const isChecked =
    selectedIds?.ids.some((id) => String(id) === String(data.id)) ?? false
  const isActive = true // Assuming all timesheet rows are active
  const [isHovered, setIsHovered] = React.useState(false)

  const handleMouseEnter = () => setIsHovered(true)
  const handleMouseLeave = () => setIsHovered(false)

  // Handle calendar icon click - navigate to new timesheet route with month
  const handleCalendarIconClick = (e: React.MouseEvent, monthIndex: number) => {
    e.stopPropagation() // Prevent row click

    // Determine year - use selectedYear if provided, otherwise current year
    const year = selectedYear || new Date().getFullYear()
    // Month index is 0-11, but we need 1-12 for the month
    const month = monthIndex + 1
    // Format month as YYYY-MM (e.g., 2025-11) to match the new route format
    const monthFormatted = `${year}-${String(month).padStart(2, '0')}`

    // Navigate to new route: /et/company/:companyId/driverModule/timesheet/driver/:driverId/month/:month
    const baseRoute = generatePath(driversRoutesMap.driverTimesheet, {
      companyId,
      driverId: data.id,
    })
    const route = `${baseRoute}/month/${monthFormatted}`
    history.push(route)
  }

  // Render calendar icons for calculation status
  // Show 12 icons in order (January-December): calculated months (blue calendar-check) and not calculated (gray calendar)
  // Red icons = no contract, Split-color icons = partial contract (starts/ends mid-month), Blue/Gray = full contract
  const renderCalculationStatus = () => {
    const calculations = data.calculations || Array(12).fill(false)
    const hasContract = data.hasContract || Array(12).fill(3) // Default to 3 (full contract)
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ]

    return (
      <div className="tw-flex tw-items-center tw-gap-[2px]">
        {calculations.map((isCalculated, monthIndex) => {
          const label = `Navigate to ${monthNames[monthIndex]}`
          const tooltipLabel = getPolishMonthName(monthIndex)
          const contractStatus = hasContract[monthIndex] ?? 3 // 0=no contract, 1=starts, 2=ends, 3=full

          const icon = isCalculated ? faCalendarCheckRegular : faCalendarRegular

          // Render different icon styles based on contract status
          let iconElement: React.ReactNode

          if (contractStatus === 0) {
            // No contract - solid red
            iconElement = (
              <FaIcon icon={icon} className="tw-h-5 tw-w-5 tw-text-red-600" />
            )
          } else if (contractStatus === 1) {
            // Contract starts mid-month - red (left) → blue (right)
            iconElement = (
              <span className="tw-relative tw-inline-block tw-h-5 tw-w-5">
                {/* Base icon in blue (will show on right half) */}
                <FaIcon icon={icon} className="tw-h-5 tw-w-5 tw-text-primary" />
                {/* Overlay wrapper clipped to left half with red icon */}
                <span
                  className="tw-absolute tw-left-0 tw-top-0 tw-h-5 tw-overflow-hidden"
                  style={{ width: '50%' }}
                >
                  <FaIcon
                    icon={icon}
                    className="tw-h-5 tw-w-5 tw-text-red-600"
                  />
                </span>
              </span>
            )
          } else if (contractStatus === 2) {
            // Contract ends mid-month - blue (left) → red (right)
            iconElement = (
              <span className="tw-relative tw-inline-block tw-h-5 tw-w-5">
                {/* Base icon in red (will show on right half) */}
                <FaIcon icon={icon} className="tw-h-5 tw-w-5 tw-text-red-600" />
                {/* Overlay wrapper clipped to left half with blue icon */}
                <span
                  className="tw-absolute tw-left-0 tw-top-0 tw-h-5 tw-overflow-hidden"
                  style={{ width: '50%' }}
                >
                  <FaIcon
                    icon={icon}
                    className="tw-h-5 tw-w-5 tw-text-primary"
                  />
                </span>
              </span>
            )
          } else {
            // Full contract - use calculation status colors
            const color = isCalculated ? 'tw-text-primary' : 'tw-text-neutral'
            iconElement = (
              <FaIcon icon={icon} className={`tw-h-5 tw-w-5 ${color}`} />
            )
          }

          return (
            <Tooltip
              text={tooltipLabel}
              key={`month-fragment-${data.id}-${monthIndex}`}
              asChild
            >
              <button
                type="button"
                className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 hover:tw-opacity-80"
                onClick={(e) => handleCalendarIconClick(e, monthIndex)}
                aria-label={label}
              >
                {iconElement}
              </button>
            </Tooltip>
          )
        })}
      </div>
    )
  }

  return (
    <tr
      className={`notches-border--right tw-relative tw-w-fit tw-cursor-pointer tw-border-b tw-bg-white hover:tw-z-10 hover:tw-bg-primary/10 ${isChecked ? 'tw-bg-primary/10 tw-font-medium' : ''} ${!isActive && isChecked ? 'tw-opacity-50' : !isActive ? 'tw-opacity-30' : ''}`}
      style={style}
     
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
    
      {/* NAZWISKO I IMIE */}
      <TableTextRow
        icon={
          <FaIcon
            icon={faIdBadge}
            className="tw-h-3 tw-w-3 tw-text-[#1f2937]"
          />
        }
        size="small"
      >
        {data.driverName}
      </TableTextRow>

      {/* STAN PRZELICZEŃ */}
      <td className="tw-cursor-pointer tw-px-3 tw-py-2.5 tw-text-content-base tw-opacity-90">
        <div className="tw-flex tw-items-center tw-gap-0">
          {renderCalculationStatus()}
        </div>
      </td>

      {/* KOSZTY */}
      <TableTextRow size="small">{data.costs || '0,00'}</TableTextRow>

      {/* BRUTTO (ŁĄCZNIE) */}
      <TableTextRow size="small">{data.grossTotal || '0,00'}</TableTextRow>

      {/* data ost. przeliczenia */}
      <TableTextRow size="small">
        {data.lastCalculationDate || '-'}
      </TableTextRow>
    </tr>
  )
}

export default withRouter(GroupViewRow)
